import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router'
import { CdsProvider } from '@chainalysis/design-system/core'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import App from './components/App'
const ForgotPassword = lazy(() => import('./components/ForgotPassword'))
const ResetPassword = lazy(() => import('./components/ResetPassword'))
const MultiFactorAuth = lazy(() => import('./components/MultiFactorAuth'))
const Login = lazy(() => import('./components/Login'))
import { getTheme } from './theme'
import { ErrorBoundary } from 'react-error-boundary'
import { CardLoading } from './components/CardLoading'

import '@chainalysis/design-system/fonts.css'
const Logout = lazy(() => import('./components/Logout'))
import ErrorPage from './components/ErrorPage'
import { ApolloProvider } from '@apollo/client'
import makeGQLClient from './graphql/client/client'
import { getAuthTokensFromLocalStorage, refreshAuthTokens } from './utils/AuthApi'
import { getGQLApiEnvironment, isOAuthBootstrapEnabled } from './utils/environment'
const PasswordlessRegistrationFormContainer = lazy(() =>
  import('./components/PasswordlessRegistration/PasswordlessRegistrationContainer').then(m => ({
    default: m.PasswordlessRegistrationFormContainer,
  }))
)
const RegistrationFormContainer = lazy(() =>
  import('./components/Registration/RegistrationContainer').then(m => ({ default: m.RegistrationFormContainer }))
)
import { PasswordPolicyProvider } from './contexts/PasswordPolicyContext'
const OAuthAuthorize = lazy(() => import('./components/OAuthAuthorize'))

console.info('[slogin] OAuth bootstrap enabled:', isOAuthBootstrapEnabled())

const router = createBrowserRouter([
  {
    path: '*',
    element: <ErrorPage />,
  },
  {
    path: '/',
    element: <Login />,
  },
  {
    path: '/login',
    children: [
      {
        index: true,
        element: <Login />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'mfa',
        element: <MultiFactorAuth />,
      },
      {
        path: 'logout',
        element: <Logout />,
      },
      // Gated: only active when SLOGIN_OAUTH_BOOTSTRAP_ENABLED=true (KaaS runtime config)
      // or REACT_APP_OAUTH_BOOTSTRAP_ENABLED=true (local dev / legacy S3 build) is set.
      // Path is /login/oauth/authorize to match the authorization_endpoint constructed by
      // um-be as loginPageBaseUrl + "/oauth/authorize" (loginPageBaseUrl = ".../login").
      ...(isOAuthBootstrapEnabled()
        ? [
            {
              path: 'oauth/authorize',
              element: (
                <ErrorBoundary FallbackComponent={ErrorPage}>
                  <OAuthAuthorize />
                </ErrorBoundary>
              ),
            },
          ]
        : []),
    ],
  },
  {
    // GS-4126: Self-service signup (/sign-up) disabled (Mar 2026). Redirects to /login.
    // /sign-up/register and /sign-up/passwordless are still active (invite-based flows).
    path: '/sign-up',
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'register',
        element: <RegistrationFormContainer />,
      },
      {
        path: 'passwordless',
        element: <PasswordlessRegistrationFormContainer />,
      },
    ],
  },
])

const _raw = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')
const cspNonce = _raw && _raw !== '__CSP_NONCE__' ? _raw : undefined
const emotionCache = createCache({ key: 'css', nonce: cspNonce })

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <CacheProvider value={emotionCache}>
    <CdsProvider theme={getTheme('light')}>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <ApolloProvider
          client={makeGQLClient({
            getAuthTokens: getAuthTokensFromLocalStorage,
            refreshAuthTokens: refreshAuthTokens,
            GQLApiEnvironment: getGQLApiEnvironment(),
          })}
        >
          <PasswordPolicyProvider>
            <App>
              <Suspense fallback={<CardLoading />}>
                <RouterProvider router={router} />
              </Suspense>
            </App>
          </PasswordPolicyProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </CdsProvider>
  </CacheProvider>
)
